import type { AxiosRequestConfig } from "axios";
import { RequestHttp } from "./http";
import { checkStatus } from "./helper/checkStatus";
import { markGlobalRequestError } from "@/utils/requestErrorState";

export type { CustomAxiosRequestConfig } from "./http";
export { RequestHttp } from "./http";

/** 读取 Pinia 持久化数据中的 token，迁移后由 Zustand 保持相同存储键。 */
export const getPersistedToken = () => {
    if (typeof localStorage === "undefined") {
        return "";
    }

    const storageKey = import.meta.env.VITE_USER_STORE_KEY || "zhihao-user";
    try {
        const persisted = JSON.parse(localStorage.getItem(storageKey) || "{}") as { token?: string };
        return persisted.token || "";
    } catch {
        return "";
    }
};

const requestConfig: AxiosRequestConfig = {
    // 开发环境交给 Vite 代理，生产环境使用既有 API 地址。
    baseURL: import.meta.env.DEV ? "" : import.meta.env.VITE_API_URL,
    timeout: 30000
};

/** 读取统一请求错误文案，优先保留旧 Vue 的 errors 明细顺序。 */
const getGlobalErrorMessages = (error: unknown): string[] => {
    if (!error || typeof error !== "object") return ["请求失败"];

    const apiError = error as { message?: unknown; errors?: unknown };
    const errorMessages = Array.isArray(apiError.errors)
        ? apiError.errors.flatMap(item => {
              if (!item || typeof item !== "object") return [];
              const message = (item as { message?: unknown }).message;
              return typeof message === "string" && message.trim() ? [message] : [];
          })
        : [];

    if (errorMessages.length > 0) return errorMessages;

    return typeof apiError.message === "string" && apiError.message.trim() ? [apiError.message] : ["请求失败"];
};

/** 将 Axios 错误转发给页面根节点，由 AntdApp 上下文负责渲染顶部全局消息。 */
export const notifyGlobalRequestError = (error: unknown) => {
    if (typeof window === "undefined") return;

    const status = error && typeof error === "object" ? (error as { status?: unknown }).status : undefined;
    // 401 已由 checkStatus 处理登录失效，避免再次弹出“请求失败”覆盖旧版提示。
    if (status === 401) return;

    markGlobalRequestError();

    for (const message of getGlobalErrorMessages(error)) {
        window.dispatchEvent(
            new CustomEvent("zhihao:global-message", {
                detail: { message, type: "error" }
            })
        );
    }
};

/** 主 API 客户端，保留原有 services 的 request.service 调用方式。 */
const request = new RequestHttp(requestConfig, {
    getToken: getPersistedToken,
    onError: notifyGlobalRequestError,
    // 复用旧 Vue 的 401 处理：企业微信重新走 OAuth，普通浏览器跳转登录页。
    onUnauthorized: response => {
        void checkStatus(response);
    }
});

const shippingConfig: AxiosRequestConfig = {
    ...requestConfig,
    baseURL: import.meta.env.VITE_SHIPPING_API_URL,
    headers: {
        "X-Request-From": "shipping"
    }
};

/** 物流域 API 客户端，保留旧服务层的灰度版本头语义。 */
export const shippingRequestHttp = new RequestHttp(shippingConfig, {
    getToken: getPersistedToken,
    onError: notifyGlobalRequestError,
    // 物流接口同样可能触发登录失效，必须与主 API 保持一致的自动重登行为。
    onUnauthorized: response => {
        void checkStatus(response);
    }
});

export default request;
